import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import KPITiles from "@/components/KPITiles";
import StatsScopeToggle from "@/components/StatsScopeToggle";
import OrdersTable from "@/components/OrdersTable";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, Sparkles, Route as RouteIcon, RefreshCw, Trash2, Play, Flag, Map as MapIcon, Plus, GripVertical, MoreVertical, ArrowUpToLine, ArrowDownToLine, ArrowUpDown, X } from "lucide-react";
import { fmtDateTime, fmtTime } from "@/lib/datetime";
import { fmtDistance, fmtEta } from "@/lib/units";
import { makeScopeFilter } from "@/lib/scope";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import MapView from "@/components/MapView";
import SelectableOrdersMap from "@/components/SelectableOrdersMap";
import PickupEditor from "@/components/PickupEditor";
import { PickupRows, toPickupPayload } from "@/components/PickupRows";
import useOrderEvents from "@/hooks/useOrderEvents";

export default function Dispatcher() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [orders, setOrders] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [startLocations, setStartLocations] = useState([]);
  const [company, setCompany] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [filterPh, setFilterPh] = useState("all");
  const [search, setSearch] = useState("");
  // Debounce keystrokes → backend so Order # search finds bulk-imported rows
  // that live past the client-side 1000-row window.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const [selected, setSelected] = useState(new Set());
  const [startId, setStartId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [label, setLabel] = useState("");
  // Optional mid-route pickup pharmacy + how many packages the driver
  // should grab on arrival. Only used in Manual mode.
  const [pickupRows, setPickupRows] = useState([{ pharmacy_id: "", qty: "" }]);
  // Manual = current single-driver flow. Automatic = bulk K-means split into
  // many driverless routes which the dispatcher staffs afterwards.
  const [buildMode, setBuildMode] = useState("manual"); // "manual" | "automatic"
  const [autoRouteCount, setAutoRouteCount] = useState(5);
  const [busy, setBusy] = useState(false);
  const [activeRoute, setActiveRoute] = useState(null);
  const [driverLoc, setDriverLoc] = useState(null);
  const [pickerMode, setPickerMode] = useState("list"); // "list" | "map"
  // Quick filter chip for the Plan Routes tab so dispatch can hide today's
  // yellow same-day pins and plan tomorrow first (or vice-versa). Value:
  //   "all"       → every unassigned order (default)
  //   "same_day"  → deliver_type == "same_day" only
  //   "next_day"  → deliver_type == "next_day" only
  // Applied client-side on top of the pharmacy + search filters so the DB
  // fetch stays unchanged.
  const [deliverFilter, setDeliverFilter] = useState("all");
  const [scope, setScope] = useState("week"); // "month" | "week"

  // Routes-tab UI: which sub-tab is showing + filters for the Route List sub-tab.
  const [tab, setTab] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get("tab") === "list" ? "list" : "plan";
  });
  const [listStatus, setListStatus] = useState("all"); // "all" | "planned" | "in_progress" | "completed"
  const [listDriver, setListDriver] = useState("all"); // "all" | driver_id | "unassigned"
  const [listDateFrom, setListDateFrom] = useState(""); // YYYY-MM-DD
  const [listDateTo, setListDateTo] = useState("");     // YYYY-MM-DD
  const [listSearch, setListSearch] = useState("");
  const resetListFilters = () => {
    setListStatus("all");
    setListDriver("all");
    setListDateFrom("");
    setListDateTo("");
    setListSearch("");
  };

  // Drag-and-drop state for reordering an active route's stops.
  // dragIdxRef = index of the row currently being dragged (set on dragStart).
  // dragOverIdx = index currently hovered over (drives the dashed highlight).
  const dragIdxRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const reorderStops = async (fromIdx, toIdx) => {
    if (fromIdx == null || toIdx == null || fromIdx === toIdx || !activeRoute) return;
    const next = [...activeRoute.stops];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    // Optimistic UI: renumber sequence so the badges + map update INSTANTLY,
    // before the server round-trip.
    const optimistic = next.map((s, i) => ({ ...s, sequence: i + 1 }));
    setActiveRoute({ ...activeRoute, stops: optimistic });
    // Build the mixed-payload the server expects. Delivery stops key by
    // order_id; pharmacy-pickup stops key by `pickup:<pharmacy_id>` so we
    // can also move them like any other row.
    const stopKey = (s) => (s.is_pickup ? `pickup:${s.pharmacy_id}` : s.order_id);
    try {
      const { data } = await api.post(`/routes/${activeRoute.id}/reorder`, {
        stop_keys: optimistic.map(stopKey).filter(Boolean),
      });
      // Server returned the canonical route with re-routed polyline + new distance/duration.
      setActiveRoute(data);
      toast.success("Route reordered");
    } catch (e) {
      // Roll back if the server rejected the change.
      setActiveRoute(activeRoute);
      toast.error(`Could not reorder: ${formatApiError(e)}`);
    }
  };

  // Convenience helpers driven by the kebab menu on each stop row.
  const moveStopToTop = (idx) => reorderStops(idx, 0);
  const moveStopToBottom = (idx) => reorderStops(idx, (activeRoute?.stops?.length ?? 1) - 1);

  const removeStopFromRoute = async (stop) => {
    if (!activeRoute) return;
    if (!window.confirm(`Remove ${stop.customer_name} from this route?\nThe order goes back to "Unassigned" so you can put it on a different route later.`)) return;
    // Optimistic: drop the row + renumber so the map updates instantly.
    const remaining = activeRoute.stops
      .filter((s) => s.order_id !== stop.order_id)
      .map((s, i) => ({ ...s, sequence: i + 1 }));
    setActiveRoute({ ...activeRoute, stops: remaining });
    try {
      const { data } = await api.post(`/routes/${activeRoute.id}/remove-stop`, {
        order_id: stop.order_id,
      });
      setActiveRoute(data);
      toast.success(`Removed ${stop.customer_name} — order is unassigned again`);
      // Make the unassigned-orders side panel refresh so the pulled stop shows up.
      load();
    } catch (e) {
      setActiveRoute(activeRoute);
      toast.error(`Could not remove: ${formatApiError(e)}`);
    }
  };

  // Poll driver location while an in-progress route is active
  useEffect(() => {
    if (!activeRoute || activeRoute.status !== "in_progress" || !activeRoute.driver_id) {
      setDriverLoc(null);
      return;
    }
    let cancelled = false;
    const fetchLoc = async () => {
      try {
        const { data } = await api.get(`/driver/location/${activeRoute.driver_id}`);
        if (!cancelled) setDriverLoc(data && data.lat ? data : null);
      } catch { /* no location yet */ }
    };
    fetchLoc();
    const id = setInterval(fetchLoc, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeRoute?.id, activeRoute?.status, activeRoute?.driver_id]);

  const load = () => {
    const params = { unassigned: true };
    if (filterPh !== "all") params.pharmacy_id = filterPh;
    if (debouncedSearch) params.search = debouncedSearch;
    Promise.all([
      api.get("/stats/overview", { params: { scope } }),
      api.get("/orders", { params }),
      api.get("/routes"),
    ]).then(([s, o, r]) => { setStats(s.data); setOrders(o.data); setRoutes(r.data); });
  };

  useEffect(() => {
    Promise.all([
      api.get("/pharmacies"),
      api.get("/users", { params: { role: "driver" } }),
      api.get("/start_locations").catch(() => ({ data: [] })),
      api.get("/company").catch(() => ({ data: null })),
    ]).then(([p, d, sl, c]) => {
      setPharmacies(p.data);
      setDrivers(d.data);
      setStartLocations(sl.data || []);
      setCompany(c.data || null);
      if (p.data[0]) setStartId(p.data[0].id);
      if (d.data[0]) setDriverId(d.data[0].id);
    });
  }, []);
  useEffect(() => { load(); }, [filterPh, scope, debouncedSearch]);
  useEffect(() => {
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [filterPh, scope, debouncedSearch]);

  // Real-time push from drivers: the moment a POD lands (Delivered / Failed),
  // merge the new fields into the local orders list so the row pill flips and
  // any active-route stop marker recolors on the next render.
  useOrderEvents((update) => {
    setOrders((prev) => {
      const idx = prev.findIndex((o) => o.id === update.id);
      if (idx < 0) return prev;
      const next = prev.slice();
      next[idx] = { ...prev[idx], ...update };
      return next;
    });
  });

  // Recent Routes filters: status, driver, date range, free-text search,
  // plus the Today/Week/Month/YTD pill (now lives on this sub-tab).
  const scopedRoutes = useMemo(() => {
    const fromTs = listDateFrom ? new Date(listDateFrom + "T00:00:00").getTime() : null;
    const toTs = listDateTo ? new Date(listDateTo + "T23:59:59.999").getTime() : null;
    const q = listSearch.trim().toLowerCase();
    const scopeFn = makeScopeFilter(scope);
    return routes.filter((r) => {
      if (!scopeFn(r)) return false;
      if (listStatus !== "all" && r.status !== listStatus) return false;
      if (listDriver === "unassigned" && r.driver_id) return false;
      if (listDriver !== "all" && listDriver !== "unassigned" && r.driver_id !== listDriver) return false;
      if (fromTs || toTs) {
        const t = r.created_at ? new Date(r.created_at).getTime() : 0;
        if (fromTs && t < fromTs) return false;
        if (toTs && t > toTs) return false;
      }
      if (q) {
        const hay = `${r.label || ""} ${r.driver_name || ""} ${r.id || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [routes, scope, listStatus, listDriver, listDateFrom, listDateTo, listSearch]);

  const hasListFilters = !!(
    listStatus !== "all" ||
    listDriver !== "all" ||
    listDateFrom ||
    listDateTo ||
    listSearch.trim()
  );

  const filtered = orders.filter((o) => {
    // Deliver-type quick filter (Plan Routes toolbar).
    if (deliverFilter !== "all") {
      const dt = (o.deliver_type || "").toLowerCase();
      if (dt !== deliverFilter) return false;
    }
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      o.customer_name?.toLowerCase().includes(q) ||
      o.address?.toLowerCase().includes(q) ||
      o.customer_phone?.toLowerCase().includes(q) ||
      o.driver_name?.toLowerCase().includes(q) ||
      o.pharmacy_name?.toLowerCase().includes(q) ||
      o.order_number?.toLowerCase().includes(q)
    );
  });

  // Auto-suggest route driver when all selected orders share the same pre-assigned driver
  useEffect(() => {
    if (selected.size === 0) return;
    const selectedOrders = orders.filter((o) => selected.has(o.id));
    const dids = Array.from(new Set(selectedOrders.map((o) => o.driver_id).filter(Boolean)));
    if (dids.length === 1) setDriverId(dids[0]);
  }, [selected, orders]);

  // Mixed-driver warning for current selection
  const mixedDriverWarning = (() => {
    if (selected.size === 0) return null;
    const dids = Array.from(new Set(orders.filter((o) => selected.has(o.id)).map((o) => o.driver_id).filter(Boolean)));
    if (dids.length > 1) {
      const names = drivers.filter((d) => dids.includes(d.id)).map((d) => d.name).join(", ");
      return `Selected orders have different pre-assigned drivers (${names}). They will all be reassigned to the route driver below.`;
    }
    return null;
  })();

  // Orders eligible for Plan Route: pending (unassigned) OR failed (any
  // attempt count). Twice-failed orders used to be blocked here and sent
  // to "Returned to Pharmacy" — as of 2026-10-01 Super Admin / Dispatcher
  // can route them anyway (useful when the patient had a brief wrong
  // address and is now reachable). The buildRoute / buildRoutesBulk
  // helpers detect this case and pass `force_retry=true` + a confirm
  // prompt before the API call. (See /routes/build + /routes/bulk-build
  // backend guardrails.)
  const isRoutable = (o) => (o.status === "pending" || o.status === "failed");

  // Helper: do any currently-selected orders need the force_retry override?
  const anyDoubleFailedSelected = () => {
    for (const id of selected) {
      const o = orders.find((x) => x.id === id);
      if (o && o.status === "failed" && Number(o.failed_count || 0) >= 2) return true;
    }
    return false;
  };
  const confirmForceRetry = () => {
    if (!anyDoubleFailedSelected()) return true;
    return window.confirm(
      "One or more selected orders have already FAILED 2 or more times. " +
      "Normally those should be Returned to the Pharmacy.\n\n" +
      "Route them anyway? This override will be logged in each order's history."
    );
  };

  const toggle = (id) => {
    const o = orders.find((x) => x.id === id);
    if (!o || !isRoutable(o)) {
      toast.message("Only pending or failed orders can be added to a new route");
      return;
    }
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    const routableIds = filtered.filter(isRoutable).map((o) => o.id);
    setSelected((s) => {
      const allSel = routableIds.length > 0 && routableIds.every((id) => s.has(id));
      return allSel ? new Set() : new Set(routableIds);
    });
  };

  const buildRoutesBulk = async () => {
    if (!startId) { toast.error("Pick a start location"); return; }
    if (selected.size === 0) { toast.error("Select at least one order"); return; }
    if (autoRouteCount < 1) { toast.error("Route quantity must be at least 1"); return; }
    const force_retry = anyDoubleFailedSelected();
    if (force_retry && !confirmForceRetry()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/routes/bulk-build", {
        start_pharmacy_id: startId,
        order_ids: Array.from(selected),
        route_count: autoRouteCount,
        force_retry,
      });
      toast.success(`${data.count} route${data.count === 1 ? "" : "s"} created — assign drivers below`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const buildRoute = async () => {
    if (!startId) return toast.error("Choose a start pharmacy");
    if (!driverId) return toast.error("Choose a driver");
    if (selected.size === 0) return toast.error("Select at least one order");
    const pickups = toPickupPayload(pickupRows);
    if (pickups.some((p) => p.pharmacy_id === startId)) {
      return toast.error("Pickup pharmacy must differ from the Start Location");
    }
    const force_retry = anyDoubleFailedSelected();
    if (force_retry && !confirmForceRetry()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/routes/build", {
        start_pharmacy_id: startId,
        driver_id: driverId,
        order_ids: Array.from(selected),
        label: label || undefined,
        pickups,
        force_retry,
      });
      toast.success(`Route built — ${data.stops.length} stops, ${fmtDistance(data.distance_km)}`);
      setActiveRoute(data);
      setSelected(new Set());
      setLabel("");
      setPickupRows([{ pharmacy_id: "", qty: "" }]);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const viewRoute = async (id) => {
    const { data } = await api.get(`/routes/${id}`);
    setActiveRoute(data);
    setTimeout(() => {
      document.querySelector('[data-testid="active-route-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };
  const startRoute = async (id) => {
    await api.post(`/routes/${id}/start`); toast.success("Route started"); load(); viewRoute(id);
  };
  // Finish-route confirm dialog state
  const [finishTarget, setFinishTarget] = useState(null); // route obj
  const [finishOpenStops, setFinishOpenStops] = useState({ count: 0, stops: [] });
  const [finishAction, setFinishAction] = useState("keep"); // "keep" | "unassign"
  const [finishBusy, setFinishBusy] = useState(false);
  const openFinishDialog = async (route) => {
    setFinishTarget(route);
    setFinishOpenStops({ count: 0, stops: [] });
    setFinishAction("keep");
    try {
      const { data } = await api.get(`/routes/${route.id}/open-stops`);
      setFinishOpenStops(data);
    } catch (e) {
      // Endpoint failure shouldn't block the dialog — show 0 and let the user decide.
      setFinishOpenStops({ count: 0, stops: [] });
    }
  };
  const confirmFinishRoute = async () => {
    if (!finishTarget) return;
    setFinishBusy(true);
    try {
      const { data } = await api.post(`/routes/${finishTarget.id}/finish`, {
        unassign_open_stops: finishAction === "unassign",
      });
      if (data.unassigned > 0) {
        toast.success(`Route completed — ${data.unassigned} stop${data.unassigned === 1 ? "" : "s"} returned to unassigned pool`);
      } else {
        toast.success("Route completed");
      }
      if (activeRoute?.id === finishTarget.id) setActiveRoute(null);
      setFinishTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFinishBusy(false);
    }
  };
  const deleteRoute = async (id) => {
    if (!confirm("Delete route and unassign its orders?")) return;
    await api.delete(`/routes/${id}`); toast.success("Route deleted"); setActiveRoute(null); load();
  };

  const reoptimizeRoute = async (id) => {
    try {
      const { data } = await api.post(`/routes/${id}/reoptimize`);
      toast.success(`Re-optimized — ${data.stops.length} stops, ${fmtDistance(data.distance_km)}`);
      setActiveRoute(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reverseRoute = async (id) => {
    // Flip the stop order (1,2,3 → 3,2,1) — planned routes only. The
    // backend rejects in-progress/completed routes so the operator can't
    // accidentally scramble a run that's already partially delivered.
    try {
      const { data } = await api.post(`/routes/${id}/reverse`);
      toast.success(`Reversed — stop order flipped (${data.stops.length} stops)`);
      setActiveRoute(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeRouteDriver = async (routeId, newDriverId) => {
    try {
      const { data } = await api.patch(`/routes/${routeId}`, { driver_id: newDriverId });
      toast.success(`Driver updated → ${data.driver_name}`);
      if (activeRoute?.id === routeId) setActiveRoute(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDeleteOrder = async (o) => {
    if (!confirm(`Delete order for ${o.customer_name}?`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Order deleted"); load(); }
    catch (e) { toast.error("Could not delete order"); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1700px] mx-auto" data-testid="dispatcher-page">
      <header className="mb-6">
        <h1 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">Dispatch board</h1>
        <p className="text-muted-foreground mt-1.5">Unassigned orders only — select them on the right to build a combined route.</p>
      </header>

      <Tabs value={tab} onValueChange={(v) => {
        setTab(v);
        const sp = new URLSearchParams(window.location.search);
        if (v === "plan") sp.delete("tab"); else sp.set("tab", v);
        const next = sp.toString();
        window.history.replaceState({}, "", next ? `?${next}` : window.location.pathname);
      }} className="w-full">
        <TabsList className="mb-6" data-testid="routes-tabs">
          <TabsTrigger value="plan" data-testid="routes-tab-plan">Plan Routes</TabsTrigger>
          <TabsTrigger value="list" data-testid="routes-tab-list">Route List</TabsTrigger>
        </TabsList>

        <TabsContent value="plan" data-testid="routes-tab-plan-content">
        <div className="mb-3">
          <span className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground">Resume of day</span>
        </div>
      <KPITiles
        items={[
          { label: "Active routes", value: stats.active_routes, hint: "Planned + running", to: `${user?.role === "super_admin" ? "/admin" : "/dispatch"}/routes?tab=list` },
          { label: "On Route", value: stats.routed, hint: "Orders on a route", color: "sky", to: "/dispatch/orders?status=on_route" },
          { label: "Unassigned", value: stats.out_of_route, hint: "Out of route", color: "yellow", to: "/dispatch/orders?status=unassigned" },
          { label: "Delivered", value: stats.delivered, hint: "Completed", color: "green", to: "/dispatch/orders?status=delivered" },
          { label: "Failed", value: stats.failed, hint: "Could not deliver", color: "red", to: "/dispatch/orders?status=failed" },
          { label: "Returned", value: stats.returned, hint: "Returned to pharmacy", color: "orange", to: "/dispatch/orders?status=returned" },
          {
            label: "Copays",
            value: stats.copay_collected_count ?? 0,
            hint: stats.copay_outstanding_count
              ? `${stats.copay_outstanding_count} outstanding · $${(stats.copay_outstanding_total ?? 0).toFixed(2)}`
              : `$${(stats.copay_collected_total ?? 0).toFixed(2)} collected`,
            color: "white",
            to: "/dispatch/orders?copay=collected",
          },
        ]}
      />

      <div className="grid lg:grid-cols-[380px_1fr] gap-6 mt-8 items-start">
        <Card className="p-5 rounded-xl border-border bg-card/80 sticky top-6" data-testid="route-builder">
          <h2 className="font-display font-semibold text-lg flex items-center gap-2 mb-3">
            <RouteIcon className="h-4 w-4 text-accent" /> Build a route
          </h2>
          {/* Mode toggle — Manual (single route + driver) vs Automatic
              (bulk K-means split into N driverless routes, dispatcher assigns
              drivers per-route afterwards). */}
          <div className="flex bg-secondary/60 p-1 rounded-full mb-4" data-testid="build-mode-toggle">
            <button
              type="button"
              onClick={() => setBuildMode("manual")}
              className={`flex-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                buildMode === "manual" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="build-mode-manual"
            >
              Manual
            </button>
            <button
              type="button"
              onClick={() => setBuildMode("automatic")}
              className={`flex-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                buildMode === "automatic" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="build-mode-automatic"
            >
              Automatic
            </button>
          </div>
          <div className="space-y-3 mb-4">
            <div>
              <label className="text-sm font-medium block mb-1">Start Location</label>
              <Select value={startId} onValueChange={setStartId}>
                <SelectTrigger data-testid="route-start"><SelectValue placeholder="Choose start" /></SelectTrigger>
                <SelectContent>
                  {company && company.lat != null && company.lng != null && (
                    <SelectItem key="company" value="company" data-testid="route-start-company">
                      🏢 {company.name || "Company HQ"}
                    </SelectItem>
                  )}
                  {startLocations.length > 0 && (
                    <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Saved depots</div>
                  )}
                  {startLocations.map((sl) => (
                    <SelectItem key={sl.id} value={sl.id}>🏬 {sl.name}</SelectItem>
                  ))}
                  {pharmacies.length > 0 && (
                    <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Pharmacies</div>
                  )}
                  {pharmacies.map((p) => <SelectItem key={p.id} value={p.id}>💊 {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Driver</label>
              <Select value={driverId} onValueChange={setDriverId} disabled={buildMode === "automatic"}>
                <SelectTrigger data-testid="route-driver"><SelectValue placeholder={buildMode === "automatic" ? "Assign per route after build" : "Assign driver"} /></SelectTrigger>
                <SelectContent>
                  {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {buildMode === "automatic" && (
                <p className="text-[11px] text-muted-foreground mt-1">Drivers attach after the routes are created.</p>
              )}
            </div>
            {buildMode === "manual" ? (
              <>
                <div>
                  <label className="text-sm font-medium">Route Name (optional)</label>
                  <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Morning route" data-testid="route-label" />
                </div>
                {/* Optional mid-route pickup pharmacy. The backend slots it
                    wherever it minimizes total mileage. */}
                <div className="md:col-span-3">
                  <label className="text-sm font-medium flex items-center gap-1.5">
                    Add Pickup Pharmacies (optional, up to 2)
                    <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-full">PICKUP</span>
                  </label>
                  <PickupRows
                    rows={pickupRows}
                    onChange={setPickupRows}
                    pharmacies={pharmacies}
                    excludeIds={startId ? [startId] : []}
                    testPrefix="route-pickup"
                    allowNone
                  />
                  {pickupRows.some((r) => r.pharmacy_id) && (
                    <p className="text-[11px] text-muted-foreground mt-1">Up to 2 pickups — the system slots each pickup stop where it adds the fewest miles.</p>
                  )}
                </div>
              </>
            ) : (
              <div>
                <label className="text-sm font-medium">Route Quantity</label>
                <Input
                  type="number"
                  min={1}
                  value={autoRouteCount}
                  onChange={(e) => setAutoRouteCount(Math.max(1, parseInt(e.target.value || "1", 10)))}
                  data-testid="route-auto-count"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Splits {selected.size} selected stop{selected.size === 1 ? "" : "s"} across {autoRouteCount} route{autoRouteCount === 1 ? "" : "s"} (~{Math.max(1, Math.ceil(selected.size / Math.max(1, autoRouteCount)))} stops each) using geographic clustering.
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between text-sm border-t border-border pt-3 mb-1">
            <span className="text-muted-foreground">Selected pending</span>
            <span className="font-mono-num font-semibold" data-testid="selected-count">
              {selected.size}{buildMode === "manual" && <span className="text-muted-foreground font-normal"> / 50</span>}
            </span>
          </div>
          {buildMode === "manual" && selected.size > 50 && (
            <div className="text-[11px] text-destructive mb-2" data-testid="route-limit-warning">
              Routes can include up to 50 stops. Please deselect {selected.size - 50} or switch to Automatic to split them.
            </div>
          )}
          {mixedDriverWarning && (
            <div className="text-[11px] text-[#9c4500] bg-[#ffe9d4] border border-[#FF6D00]/45 rounded-lg px-2.5 py-1.5 mb-2" data-testid="mixed-driver-warning">
              {mixedDriverWarning}
            </div>
          )}
          {buildMode === "manual" ? (
            <Button disabled={busy || selected.size === 0 || selected.size > 50} onClick={buildRoute} className="w-full rounded-full h-11 mt-2" data-testid="build-route-btn">
              <Sparkles className="h-4 w-4 mr-1.5" /> {busy ? "Optimizing…" : "Optimize & build route"}
            </Button>
          ) : (
            <Button disabled={busy || selected.size === 0 || autoRouteCount < 1} onClick={buildRoutesBulk} className="w-full rounded-full h-11 mt-2" data-testid="build-routes-bulk-btn">
              <Sparkles className="h-4 w-4 mr-1.5" /> {busy ? "Splitting…" : `Build ${autoRouteCount} route${autoRouteCount === 1 ? "" : "s"}`}
            </Button>
          )}
        </Card>

        <div>
          <Card className="p-4 rounded-xl border-border bg-card/80 mb-3">
            <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
              <div className="inline-flex rounded-full bg-secondary/60 p-1 shrink-0" data-testid="picker-mode-switch">
                <button
                  type="button"
                  onClick={() => setPickerMode("list")}
                  className={`px-3 h-8 text-xs font-medium rounded-full transition-colors ${pickerMode === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="picker-mode-list"
                >
                  From list
                </button>
                <button
                  type="button"
                  onClick={() => setPickerMode("map")}
                  className={`px-3 h-8 text-xs font-medium rounded-full transition-colors ${pickerMode === "map" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="picker-mode-map"
                >
                  On map
                </button>
              </div>
              {pickerMode === "list" && (
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search order #, customer, address, driver…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="orders-search" />
                </div>
              )}
              {pickerMode === "map" && <div className="flex-1" />}
              <div className="w-full md:w-56">
                <Select value={filterPh} onValueChange={setFilterPh}>
                  <SelectTrigger data-testid="filter-pharmacy"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All pharmacies</SelectItem>
                    {pharmacies.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* Deliver-type quick filter. Matches the pin color language on
                  the map — yellow chip for Same Day, amber for Next Day. */}
              <div className="inline-flex rounded-full bg-secondary/60 p-1 shrink-0" data-testid="plan-deliver-filter">
                <button
                  type="button"
                  onClick={() => setDeliverFilter("all")}
                  className={`px-3 h-8 text-xs font-medium rounded-full transition-colors ${deliverFilter === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="plan-deliver-filter-all"
                  aria-pressed={deliverFilter === "all"}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setDeliverFilter("same_day")}
                  className={`px-3 h-8 text-xs font-semibold rounded-full transition-colors flex items-center gap-1.5 ${
                    deliverFilter === "same_day"
                      ? "bg-yellow-400 text-yellow-950 shadow"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="plan-deliver-filter-same-day"
                  aria-pressed={deliverFilter === "same_day"}
                  title="Show only Same Day unassigned orders"
                >
                  <span className="h-2 w-2 rounded-full" style={{ background: "#EAB308" }} />
                  Same Day
                </button>
                <button
                  type="button"
                  onClick={() => setDeliverFilter("next_day")}
                  className={`px-3 h-8 text-xs font-semibold rounded-full transition-colors flex items-center gap-1.5 ${
                    deliverFilter === "next_day"
                      ? "bg-amber-400 text-amber-950 shadow"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="plan-deliver-filter-next-day"
                  aria-pressed={deliverFilter === "next_day"}
                  title="Show only Next Day unassigned orders — plan tomorrow first"
                >
                  <span className="h-2 w-2 rounded-full" style={{ background: "#F59E0B" }} />
                  Next Day
                </button>
              </div>
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium shrink-0 px-2">Unassigned only</span>
            </div>
          </Card>

          {pickerMode === "list" ? (
            <>
              <OrdersTable
                orders={filtered}
                selectable
                selected={selected}
                onToggle={toggle}
                onToggleAll={toggleAll}
                openRoutes={routes.filter((r) => ["planned", "in_progress", "paused"].includes(r.status))}
                onAddToRoute={async (o, routeId) => {
                  try {
                    await api.post(`/routes/${routeId}/add-order`, { order_id: o.id });
                    toast.success(`${o.customer_name} added to route`);
                    await load();
                  } catch (e) {
                    toast.error(formatApiError(e));
                  }
                }}
                onDelete={onDeleteOrder}
                hideActions
                emptyMessage="No orders match these filters."
              />
              <div className="text-xs text-muted-foreground mt-2 font-mono-num">{filtered.length} of {orders.length} orders</div>
            </>
          ) : (
            <SelectableOrdersMap
              orders={filtered}
              pharmacies={pharmacies}
              selected={selected}
              onToggle={toggle}
              height={560}
            />
          )}
        </div>
      </div>
      </TabsContent>

      <TabsContent value="list" data-testid="routes-tab-list-content">
      <section>
        {/* Scope pill (Today/Week/Month/YTD) moved here from Plan Routes. */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground">Date scope</span>
          <StatsScopeToggle value={scope} onChange={setScope} />
        </div>
        {/* Route List filters: date range, status, driver, search. */}
        <Card className="p-4 rounded-xl border-border bg-card/80 mb-4" data-testid="route-list-filters">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Route label, driver, or id…"
                  className="pl-9"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                  data-testid="route-list-search"
                />
              </div>
            </div>
            <div className="w-40">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Status</label>
              <Select value={listStatus} onValueChange={setListStatus}>
                <SelectTrigger data-testid="route-list-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">Driver</label>
              <Select value={listDriver} onValueChange={setListDriver}>
                <SelectTrigger data-testid="route-list-driver"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All drivers</SelectItem>
                  <SelectItem value="unassigned">— Unassigned —</SelectItem>
                  {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">From</label>
              <Input
                type="date"
                value={listDateFrom}
                onChange={(e) => setListDateFrom(e.target.value)}
                data-testid="route-list-date-from"
              />
            </div>
            <div className="w-40">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">To</label>
              <Input
                type="date"
                value={listDateTo}
                onChange={(e) => setListDateTo(e.target.value)}
                data-testid="route-list-date-to"
              />
            </div>
            {hasListFilters && (
              <Button
                variant="ghost"
                onClick={resetListFilters}
                className="h-10 text-xs text-muted-foreground hover:text-foreground"
                data-testid="route-list-clear-filters"
              >
                <X className="h-3 w-3 mr-1" /> Clear filters
              </Button>
            )}
          </div>
        </Card>

        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-semibold text-lg">Recent routes</h2>
          <span className="text-xs text-muted-foreground font-mono-num" data-testid="recent-routes-count">
            {scopedRoutes.length} of {routes.length} route(s){hasListFilters ? " · filtered" : ""}
          </span>
        </div>
        <Card className="rounded-xl border-border bg-card/80 overflow-hidden">
          <ul className="divide-y divide-border" data-testid="routes-list">
            {scopedRoutes.map((r) => {
              const statusCls =
                r.status === "completed" ? "bg-accent/15 text-accent" :
                r.status === "in_progress" ? "bg-[#3a86ff]/15 text-[#1f4ea3]" : "bg-secondary text-secondary-foreground";
              const active = activeRoute?.id === r.id;
              return (
                <li
                  key={r.id}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${active ? "bg-primary/5" : "hover:bg-secondary/40"}`}
                  onClick={() => viewRoute(r.id)}
                  data-testid={`route-row-${r.id}`}
                >
                  <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${active ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>
                    <RouteIcon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-[1.4fr_180px_1fr_auto] gap-3 items-center">
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-sm truncate">{r.label}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        Created {fmtDateTime(r.created_at)}
                      </div>
                    </div>
                    <div onClick={(e) => e.stopPropagation()} data-testid={`route-driver-${r.id}`}>
                      <span className="text-muted-foreground text-[10px] uppercase tracking-wider block mb-1">Driver</span>
                      <Select
                        value={r.driver_id || ""}
                        onValueChange={(v) => changeRouteDriver(r.id, v)}
                        disabled={r.status === "completed"}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`route-driver-select-${r.id}`}>
                          <SelectValue placeholder="Pick driver" />
                        </SelectTrigger>
                        <SelectContent>
                          {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-xs font-mono-num text-muted-foreground">
                      <span className="text-muted-foreground text-[10px] uppercase tracking-wider block">Stops · Distance · ETA</span>
                      {r.stops?.length || 0} · {fmtDistance(r.distance_km)} · {fmtEta(r.duration_min, r.stops?.length || 0)}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${statusCls}`}>
                      {r.status?.replace("_", " ")}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-primary h-9 w-9"
                      onClick={() => viewRoute(r.id)}
                      title="View route on map"
                      data-testid={`view-route-${r.id}`}
                    >
                      <MapIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-primary h-9 w-9"
                      onClick={() => reoptimizeRoute(r.id)}
                      title="Re-optimize route"
                      data-testid={`reoptimize-route-${r.id}`}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                    {r.stops.length >= 2 &&
                      (r.status === "planned" ||
                        ((r.status === "in_progress" || r.status === "paused") &&
                          !r.stops.some((s) => ["delivered", "failed", "returned", "picked_up"].includes(s.status)))) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-primary h-9 w-9"
                        onClick={() => reverseRoute(r.id)}
                        title="Invert stop order (available until the first stop is delivered)"
                        data-testid={`reverse-route-${r.id}`}
                      >
                        <ArrowUpDown className="h-4 w-4" />
                      </Button>
                    )}
                    {r.status === "planned" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-accent h-9 w-9"
                        onClick={() => startRoute(r.id)}
                        title="Start route"
                        data-testid={`start-route-${r.id}`}
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                    )}
                    {user?.role === "super_admin" && r.status !== "completed" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-accent h-9 w-9"
                        onClick={() => openFinishDialog(r)}
                        title="Finish route (mark as completed)"
                        data-testid={`finish-route-${r.id}`}
                      >
                        <Flag className="h-4 w-4" />
                      </Button>
                    )}
                    {r.status !== "completed" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive h-9 w-9"
                        onClick={() => deleteRoute(r.id)}
                        title="Delete route"
                        data-testid={`delete-route-${r.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
            {routes.length === 0 && (
              <li className="px-4 py-8 text-sm text-muted-foreground text-center" data-testid="routes-empty-state">
                No routes yet — select pending orders and build one on the left.
              </li>
            )}
            {routes.length > 0 && scopedRoutes.length === 0 && (
              <li className="px-4 py-8 text-sm text-muted-foreground text-center" data-testid="routes-empty-scope">
                No routes match these filters. Try clearing them above.
              </li>
            )}
          </ul>
        </Card>
      </section>

      {activeRoute && (
        <section className="mt-8" data-testid="active-route-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-display font-semibold text-lg">{activeRoute.label}</h2>
              <p className="text-xs text-muted-foreground">{activeRoute.stops.length} stops · {fmtDistance(activeRoute.distance_km)} · ~{fmtEta(activeRoute.duration_min, activeRoute.stops.length)} · {activeRoute.driver_name}</p>
            </div>
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground hidden md:inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-secondary/60">
              <GripVertical className="h-3 w-3" /> Drag rows to reorder
            </span>
          </div>

          {/* Pickup Pharmacy editor — change/remove/add the mid-route pickup
              stop on an already-built route. The backend re-runs OSRM and
              re-slots the pickup at the optimal index. */}
          <PickupEditor
            activeRoute={activeRoute}
            pharmacies={pharmacies}
            onSaved={(r) => { setActiveRoute(r); load(); }}
          />

          <MapView
            start={activeRoute.start}
            stops={activeRoute.stops.map((s) => {
              // Prefer the order's current status from the locally-cached
              // orders list (kept fresh by the 12s poll). Fall back to the
              // status the backend embedded on each stop in GET /routes/{id}
              // — that path covers routes whose orders are no longer in the
              // "unassigned" feed (e.g. completed routes with delivered /
              // failed / returned orders).
              const o = orders.find((x) => x.id === s.order_id);
              return {
                ...s,
                status: o?.status ?? s.status,
                failed_count: o?.failed_count ?? s.failed_count ?? 0,
                pharmacy_nickname: o?.pharmacy_nickname || o?.pharmacy_name || s.pharmacy_nickname || "",
              };
            })}
            polyline={activeRoute.polyline}
            height={500}
            driverLocation={driverLoc}
            routeStatus={activeRoute.status}
            routeLabel={activeRoute.label}
            // Tight fit to the route's actual N/S/E/W stops — no regional
            // baseline padding. Matches the Plan Routes On-Map behavior.
            fitToStopsOnly
          />
          {activeRoute.status === "in_progress" && (
            <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
              <span className={`inline-block h-2 w-2 rounded-full ${driverLoc ? "bg-[#3a86ff] animate-pulse" : "bg-muted"}`}></span>
              {driverLoc
                ? <>Driver location updated {fmtTime(driverLoc.at)}</>
                : <>Waiting for driver to enable GPS sharing…</>}
            </div>
          )}
          <ol
            className="mt-4 space-y-2"
            data-testid="active-route-stops"
            onDragOver={(e) => e.preventDefault()}
          >
            {activeRoute.stops.map((s, idx) => {
              const o = orders.find((x) => x.id === s.order_id);
              const pharmacyNick = o?.pharmacy_nickname || o?.pharmacy_name || s.pharmacy_nickname || s.pharmacy_name || "";
              return (
              <li
                key={s.order_id || `pickup-${s.pharmacy_id}-${s.sequence}`}
                draggable
                onDragStart={() => { dragIdxRef.current = idx; }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragOverIdx !== idx) setDragOverIdx(idx);
                }}
                onDragLeave={() => setDragOverIdx((cur) => (cur === idx ? null : cur))}
                onDrop={(e) => {
                  e.preventDefault();
                  reorderStops(dragIdxRef.current, idx);
                  dragIdxRef.current = null;
                  setDragOverIdx(null);
                }}
                onDragEnd={() => { dragIdxRef.current = null; setDragOverIdx(null); }}
                className={`flex items-start gap-3 p-3 rounded-lg bg-card border transition-all cursor-grab active:cursor-grabbing ${
                  dragOverIdx === idx ? "border-primary border-2 bg-primary/5 -translate-y-px shadow-md" : "border-border"
                }`}
                data-testid={`stop-row-${s.order_id || `pickup-${s.pharmacy_id}`}`}
              >
                <GripVertical className="h-4 w-4 text-muted-foreground/60 mt-1.5 shrink-0" />
                <div className={`h-7 w-7 rounded-full grid place-items-center text-xs font-bold font-mono-num shrink-0 ${s.is_pickup ? "bg-amber-500 text-white ring-2 ring-amber-300/60" : "bg-primary text-primary-foreground"}`}>{s.sequence}</div>
                <div className="flex-1 min-w-0">
                  {pharmacyNick && (
                    <div
                      className="text-xs font-medium text-primary/80 truncate"
                      data-testid={`stop-pharmacy-${s.order_id || `pickup-${s.pharmacy_id}`}`}
                      title={pharmacyNick}
                    >
                      {pharmacyNick}
                    </div>
                  )}
                  <div className="font-medium text-sm">
                    {s.is_pickup && <span className="text-[10px] uppercase tracking-wide mr-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">Pickup</span>}
                    {s.customer_name}
                  </div>
                  <div className="text-xs text-muted-foreground">{s.address}</div>
                </div>
                {!s.is_pickup && (
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">{s.priority?.replace("_", " ")}</span>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground"
                      data-testid={`stop-actions-${s.order_id || `pickup-${s.pharmacy_id}`}`}
                      title="Stop actions"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem
                      disabled={idx === 0}
                      onSelect={() => moveStopToTop(idx)}
                      data-testid={`stop-move-top-${s.order_id || `pickup-${s.pharmacy_id}`}`}
                    >
                      <ArrowUpToLine className="h-4 w-4 mr-2" /> Move to top
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={idx === activeRoute.stops.length - 1}
                      onSelect={() => moveStopToBottom(idx)}
                      data-testid={`stop-move-bottom-${s.order_id || `pickup-${s.pharmacy_id}`}`}
                    >
                      <ArrowDownToLine className="h-4 w-4 mr-2" /> Move to bottom
                    </DropdownMenuItem>
                    {!s.is_pickup && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => removeStopFromRoute(s)}
                          className="text-destructive focus:text-destructive"
                          data-testid={`stop-remove-${s.order_id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> Remove from route
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
              );
            })}
          </ol>
        </section>
      )}
      </TabsContent>
      </Tabs>

      {/* Super-admin: finish-route confirm dialog */}
      <Dialog open={!!finishTarget} onOpenChange={(o) => { if (!o && !finishBusy) setFinishTarget(null); }}>
        <DialogContent data-testid="finish-route-dialog">
          <DialogHeader>
            <DialogTitle>Finish route?</DialogTitle>
          </DialogHeader>
          {finishTarget && (
            <div className="space-y-4 text-sm">
              <div className="text-muted-foreground">
                Mark <span className="font-medium text-foreground">{finishTarget.label}</span> as <span className="font-semibold text-accent">COMPLETED</span>.
              </div>
              {finishOpenStops.count > 0 ? (
                <>
                  <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs flex items-start gap-2" data-testid="finish-route-warning">
                    <span className="text-base leading-none">⚠️</span>
                    <div>
                      <div className="font-semibold mb-0.5">
                        {finishOpenStops.count} stop{finishOpenStops.count === 1 ? "" : "s"} still open on this route
                      </div>
                      <ul className="space-y-0.5">
                        {finishOpenStops.stops.slice(0, 6).map((s) => (
                          <li key={s.id} className="font-mono-num text-[11px]">
                            #{s.order_number || s.id.slice(0, 8)} · {s.customer_name} <span className="uppercase opacity-70">[{s.status.replace("_"," ")}]</span>
                          </li>
                        ))}
                        {finishOpenStops.stops.length > 6 && (
                          <li className="text-[11px] opacity-70">…and {finishOpenStops.stops.length - 6} more</li>
                        )}
                      </ul>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">What to do with the open stops?</Label>
                    <label className={`flex items-start gap-2 cursor-pointer rounded-lg border p-3 ${finishAction === "keep" ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/40"}`} data-testid="finish-route-choice-keep">
                      <input
                        type="radio"
                        name="finish-action"
                        value="keep"
                        checked={finishAction === "keep"}
                        onChange={() => setFinishAction("keep")}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="font-medium">Leave as-is</div>
                        <div className="text-xs text-muted-foreground">Stops keep their current status (assigned / in transit). Useful when the driver finished them outside the app.</div>
                      </div>
                    </label>
                    <label className={`flex items-start gap-2 cursor-pointer rounded-lg border p-3 ${finishAction === "unassign" ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/40"}`} data-testid="finish-route-choice-unassign">
                      <input
                        type="radio"
                        name="finish-action"
                        value="unassign"
                        checked={finishAction === "unassign"}
                        onChange={() => setFinishAction("unassign")}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="font-medium">Remove from route</div>
                        <div className="text-xs text-muted-foreground">Stops flip back to <span className="font-semibold">UNASSIGNED</span> (no route, no driver) so dispatch can re-route them.</div>
                      </div>
                    </label>
                  </div>
                </>
              ) : (
                <div className="text-xs text-muted-foreground">All stops on this route are already finalized — nothing else will be changed.</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFinishTarget(null)} disabled={finishBusy}>Cancel</Button>
            <Button
              onClick={confirmFinishRoute}
              disabled={finishBusy}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              data-testid="finish-route-confirm"
            >
              <Flag className="h-4 w-4 mr-1.5" /> {finishBusy ? "Finishing…" : "Finish route"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
